/* eslint-disable @typescript-eslint/naming-convention */
import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { filter, switchMap, tap } from 'rxjs/operators';
import { MenuItem } from 'primeng/api';
import { SortOrderEnum } from '../../../../api/shared/enums/sort-order.enum';
import { MonitoringSortTypesEnum, searchCriteriaSortFieldToMonitoringSortTypeMap } from '../../../../Common/MonitorShared/enum/monitoring-sort-types.enum';
import { SearchCriteriaSortFieldEnum } from '../../../../api/mentions/enum/search-criteria-sort-field.enum';
import { Subscription } from 'rxjs';
import { MonitoringRoutingService } from '../../../Core/services/monitoring-routing.service';
import { RouteType } from '../../enums/route-type.enum';
import { MentionSearchService } from '../../../Core/services/mention-search.service';
import { UserPreferencesService } from '../../../../Account/UserPreferences/services/user-preferences.service';
import { AgprComponentStyleEnum } from '../../../../Common/Basic/enums/social-analysis-types.enum';
import { SocialSearchService } from '../../../../Social/Core/services/social-search.service';
import { ActionKeys } from '../../../../Common/Forms/enums/keys.enum';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { userPermissionsEnum } from '@Core/Users/enums/user-permissions.enum';
import { MonitoringRouteModel } from '@Monitor/Shared/models/monitoring-route.model';

@Component({
    selector: 'agpr-mentions-sort-order',
    templateUrl: './mentions-sort-order.component.html',
    styleUrls: ['./mentions-sort-order.component.scss'],
    standalone: false
})
export class MentionsSortOrderComponent implements OnInit, OnDestroy {
    readonly SortOrderEnum = SortOrderEnum;
    readonly agprComponentStyle = AgprComponentStyleEnum;

    public labels = {
        [MonitoringSortTypesEnum.published]: {
            [SortOrderEnum.ascending]: 'Mentions.sortOrder.publishedOldest',
            [SortOrderEnum.descending]: 'Mentions.sortOrder.publishedNewest'
        },
        [MonitoringSortTypesEnum.available]: {
            [SortOrderEnum.ascending]: 'Mentions.sortOrder.availableOldest',
            [SortOrderEnum.descending]: 'Mentions.sortOrder.availableNewest'
        },
        [MonitoringSortTypesEnum.relevancy]: {
            [SortOrderEnum.ascending]: 'Mentions.sortOrder.relevancyLeast',
            [SortOrderEnum.descending]: 'Mentions.sortOrder.relevancyMost'
        },
        [MonitoringSortTypesEnum.audience]: {
            [SortOrderEnum.ascending]: 'Mentions.sortOrder.audienceReachLeast',
            [SortOrderEnum.descending]: 'Mentions.sortOrder.audienceReachMost'
        },
        [MonitoringSortTypesEnum.domainAuthority]: {
            [SortOrderEnum.ascending]: 'Mentions.sortOrder.domainAuthorityLeast',
            [SortOrderEnum.descending]: 'Mentions.sortOrder.domainAuthorityMost'
        },
        [MonitoringSortTypesEnum.engagement]: {
            [SortOrderEnum.ascending]: 'Mentions.sortOrder.engagementLeast',
            [SortOrderEnum.descending]: 'Mentions.sortOrder.engagementMost'
        },
        [MonitoringSortTypesEnum.engagement_rate]: {
            [SortOrderEnum.ascending]: 'Mentions.sortOrder.engagementRateLeast',
            [SortOrderEnum.descending]: 'Mentions.sortOrder.engagementRateMost'
        }
    };

    selectedMenuItem: string;
    @Input() id: string = null;
    @Input() isDisabled: boolean = false;
    @Input() style: AgprComponentStyleEnum = AgprComponentStyleEnum.social;
    @Input() showSelectedSortOrderLabel: boolean = false;

    sortByOptions: MenuItem[] = [
        {
            id: MonitoringSortTypesEnum.published,
            command: () => this.changeSortBy(SearchCriteriaSortFieldEnum.published, MonitoringSortTypesEnum.published)
        },
        {
            id: MonitoringSortTypesEnum.available,
            command: () => this.changeSortBy(SearchCriteriaSortFieldEnum.indexed_date, MonitoringSortTypesEnum.available)
        },
        {
            id: MonitoringSortTypesEnum.audience,
            command: () => this.changeSortBy(SearchCriteriaSortFieldEnum.audience_reach, MonitoringSortTypesEnum.audience)
        },
        {
            id: MonitoringSortTypesEnum.relevancy,
            command: () => this.changeSortBy(SearchCriteriaSortFieldEnum.relevancy, MonitoringSortTypesEnum.relevancy)
        },
        {
            id: MonitoringSortTypesEnum.domainAuthority,
            command: () => this.changeSortBy(SearchCriteriaSortFieldEnum.domainAuthority, MonitoringSortTypesEnum.domainAuthority)
        }
    ];

    socialSortByOptions: MenuItem[] = [
        {
            id: MonitoringSortTypesEnum.published,
            command: () => this.changeSortBy(SearchCriteriaSortFieldEnum.published, MonitoringSortTypesEnum.published)
        },
        {
            id: MonitoringSortTypesEnum.available,
            command: () => this.changeSortBy(SearchCriteriaSortFieldEnum.indexed_date, MonitoringSortTypesEnum.available)
        },
        {
            id: MonitoringSortTypesEnum.audience,
            command: () => this.changeSortBy(SearchCriteriaSortFieldEnum.audience_reach, MonitoringSortTypesEnum.audience)
        },
        {
            id: MonitoringSortTypesEnum.engagement,
            command: () => this.changeSortBy(SearchCriteriaSortFieldEnum.engagement_total, MonitoringSortTypesEnum.engagement)
        },
        {
            id: MonitoringSortTypesEnum.engagement_rate,
            command: () => this.changeSortBy(SearchCriteriaSortFieldEnum.engagement_rate, MonitoringSortTypesEnum.engagement_rate)
        }
    ];

    subscriptions: Subscription[] = [];
    sortOrderDropdownDisplay: boolean = false;
    sortOrder: SortOrderEnum;
    sortField: SearchCriteriaSortFieldEnum;
    sortLabel: string;
    ascendingSortLabel: string = 'Sort by ascending';
    descendingSortLabel: string = 'Sort by descending';

    // style that may need to be changed based on @Input style
    arrowIcon: string = 'fa-solid fa-angle-down';
    ascendingSortOrderIcon: string = 'fa-regular fa-arrow-down-short-wide';
    descendingSortOrderIcon: string = 'fa-regular fa-arrow-up-wide-short';
    sortOrderIcon: string = this.ascendingSortOrderIcon;
    searchService: MentionSearchService | SocialSearchService;
    currentRoute: MonitoringRouteModel;
    selectedSortOrderLabel: string = '';

    @ViewChild('trigger') public trigger: ElementRef;

    constructor(
        private monitoringRouterService: MonitoringRoutingService,
        private mentionSearchService: MentionSearchService,
        private userPreferencesService: UserPreferencesService,
        private socialSearchService: SocialSearchService,
        private currentUserStateService: CurrentUserStateService,
    ) {
    }

    ngOnInit(): void {
        if (this.style === AgprComponentStyleEnum.social || this.style === AgprComponentStyleEnum.socialPlus) {
            this.searchService = this.socialSearchService;
            this.sortByOptions = this.socialSortByOptions;
        } else {
            this.searchService = this.mentionSearchService;
        }
        this.setupCustomStyles();
        this.createSubscriptions();
        this.subscriptions.push(
            this.currentUserStateService.getPermissions().subscribe(permissions => {
                if (!permissions) {
                    return;
                }
                if (permissions.includes(userPermissionsEnum.socialIntelligence)) {
                    const hasEngagementOptions = this.sortByOptions.some(
                        option => option.id === MonitoringSortTypesEnum.engagement || option.id === MonitoringSortTypesEnum.engagement_rate
                    );
                    if (!hasEngagementOptions) {
                        this.sortByOptions.push(
                            {
                                id: MonitoringSortTypesEnum.engagement,
                                command: () => this.changeSortBy(SearchCriteriaSortFieldEnum.engagement_total, MonitoringSortTypesEnum.engagement)
                            },
                            {
                                id: MonitoringSortTypesEnum.engagement_rate,
                                command: () => this.changeSortBy(SearchCriteriaSortFieldEnum.engagement_rate, MonitoringSortTypesEnum.engagement_rate)
                            }
                        );
                    }
                }
            })

        );
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }

    private setupCustomStyles(): void {
        if (this.style === AgprComponentStyleEnum.social || this.style === AgprComponentStyleEnum.socialPlus) {
            this.arrowIcon = 'fa-solid fa-angle-down';
            this.ascendingSortOrderIcon = 'fa-regular fa-arrow-down-short-wide';
            this.descendingSortOrderIcon = 'fa-regular fa-arrow-up-wide-short';
            this.sortOrderIcon = this.ascendingSortOrderIcon;
        }
    }

    /**
     * Closes the dropdown for sort options
    */
    closeSortOptionDropdown() {
        this.sortOrderDropdownDisplay = false;
    }

    /**
     * Subscribes to changes to localization map in LocalizationService and rerenders
     * the components localiztions
     */
    createSubscriptions(): void {
        this.subscriptions.push(
            this.monitoringRouterService.getCurrentRoute().pipe(
                tap((route) => {
                    this.currentRoute = route;
                    switch (route.type) {
                        case RouteType.folder:
                            const index = this.sortByOptions.findIndex(
                                option => option.id === MonitoringSortTypesEnum.relevancy
                            );

                            // splice of (-1, 1) will remove the last element, so we need to do a check first
                            if (index >= 0) {
                                // remove relevancy
                                this.sortByOptions.splice(index, 1);
                            }

                            this.validateSelectedMenuItem();
                            break;
                        default:
                            break;
                    }
                }),
                switchMap(() => this.searchService.getSearchCriteria().pipe(
                    filter(x => !!x)
                ))
            ).subscribe(searchCriteria => {
                this.setSortOrder(searchCriteria.sortOrder);

                const criteriaSortField = searchCriteria.sortField;
                if (this.currentRoute?.type === RouteType.folder && criteriaSortField === SearchCriteriaSortFieldEnum.relevancy) {
                    this.validateSelectedMenuItem();
                } else {
                    this.setSortField(searchCriteria.sortField);
                }
            })
        );
    }

    private validateSelectedMenuItem() {
        // if we have something selected that's not in the menu, switch to the first option instead
        if (!this.sortByOptions.find(item => this.selectedMenuItem === item.id)) {
            this.selectedMenuItem = this.sortByOptions[0]?.id;
        }
        this.updateSelectedSortOrderLabel();
    }

    /**
     * Event Handler for displaying sortOrder Dropdown
     */
    sortOrderDropdownClick() {
        this.sortOrderDropdownDisplay = !this.sortOrderDropdownDisplay;
        if (this.sortOrderDropdownDisplay) {
            requestAnimationFrame(() => {
                (document.querySelector('li.agpr-sortOrder-dropdownPanelItem--selected') as HTMLElement)?.focus();
            });
        } else {
            this.trigger.nativeElement.focus();
        }
    }

    /**
     * Updates the sortOrder of the SearchCriteriaModel in the MonitoringSearchService
     * @param sortOrder SortOrderEnum - either ascending or descending
     */
    updateSearchServiceSortOrder(sortOrder: SortOrderEnum): void {
        this.searchService.setSortOrder(this.sortOrder);
        this.searchService.search();
    }

    /**
    * Updates the SearchCriteriaModel in the MonitoringSearchService
    * @param sortField UnifiedSearchSortByEnum - value for sorting mentions
    */
    updateSearchServiceSortField(sortField: SearchCriteriaSortFieldEnum): void {
        this.searchService.setSortField(this.sortField);
        this.searchService.search();
    }

    /**
     * Reacts to dropdown item selected.  Updates search Service, and selects sortByOption
     * @param sortBy UnifiedSearchSortByEnum
     * @param id MonitoringSortTypesEnum
     */
    changeSortBy(sortBy: SearchCriteriaSortFieldEnum, id: MonitoringSortTypesEnum): void {
        this.sortOrderDropdownClick();

        this.selectedMenuItem = id;

        this.sortField = sortBy;

        this.updateSearchServiceSortField(sortBy);

        this.updateSelectedSortOrderLabel();
    }

    /**
     * Sets ascending sort order & updates MonitoringSearchService
     */
    private setAscendingSortOrder(): void {
        this.sortOrder = SortOrderEnum.ascending;

        this.sortLabel = this.ascendingSortLabel;
        this.sortOrderIcon = this.ascendingSortOrderIcon;
        this.updateSearchServiceSortOrder(SortOrderEnum.ascending);
        this.updateSelectedSortOrderLabel();
    }

    /**
     * Sets descending sort order & updates MonitoringSearchService
     */
    private setDescendingSortOrder(): void {
        this.sortOrder = SortOrderEnum.descending;

        this.sortLabel = this.descendingSortLabel;
        this.sortOrderIcon = this.descendingSortOrderIcon;
        this.updateSearchServiceSortOrder(SortOrderEnum.descending);
        this.updateSelectedSortOrderLabel();
    }

    /**
     * Toggles sort order from Ascending -  Desending
     */
    toggleSortOrder(): void {
        if (this.sortOrder === SortOrderEnum.ascending) {
            this.setDescendingSortOrder();
        } else {
            this.setAscendingSortOrder();
        }
    }

    /**
     * Sets the selected menuitem
     */
    private setSortField(sortField: SearchCriteriaSortFieldEnum): void {
        this.sortField = sortField;

        const itemId = searchCriteriaSortFieldToMonitoringSortTypeMap.get(this.sortField);
        this.selectedMenuItem = itemId;
        this.updateSelectedSortOrderLabel();
    }

    /**
     * Sets selected sort order
     */
    private setSortOrder(sortOrder: SortOrderEnum): void {
        this.sortOrder = sortOrder;

        if (this.sortOrder === SortOrderEnum.ascending) {
            this.sortOrderIcon = this.ascendingSortOrderIcon;

        } else {
            this.sortOrderIcon = this.descendingSortOrderIcon;
        }
        this.updateSelectedSortOrderLabel();
    }

    onTriggerKeydown(event: KeyboardEvent): void {
        if (event.key === ActionKeys.DOWN && !this.sortOrderDropdownDisplay) {
            this.sortOrderDropdownClick();
        }
    }

    onOptionKeydown(event: KeyboardEvent, index: number): void {
        if (index < 0) {
            return;
        }
        if ((event.key === ActionKeys.UP && index === 0) || event.key === ActionKeys.TAB || event.key === ActionKeys.ENTER || event.key === ActionKeys.ESCAPE) {
            this.sortOrderDropdownClick();
        }
        if (event instanceof KeyboardEvent && !(event.key === ActionKeys.ENTER || event.key === ActionKeys.SPACE)) {
            return;
        }
        this.sortByOptions[index].command({originalEvent: event});
    }

    private updateSelectedSortOrderLabel(): void {
        this.selectedSortOrderLabel = this.labels[this.selectedMenuItem]?.[this.sortOrder] ?? '';
    }
}
